import { useEffect, useRef, useState } from "react";
import {
  Coins,
  LogIn,
  LogOut,
  UserRound,
} from "lucide-react";
import { useAuth } from "../contexts/auth-context";
import {
  formatTokenFa,
  getDisplayTokenBalance,
} from "../lib/token-balance";

type Props = {
  onOpenProfile?: () => void;
};

export function AppAuthChip({ onOpenProfile }: Props) {
  const { isAuthenticated, user, openLogin, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  if (!isAuthenticated) {
    const balance = getDisplayTokenBalance({
      isAuthenticated: false,
      userId: null,
    });
    return (
      <div className="chrome-auth" ref={rootRef}>
        <span
          className="token-chip chrome-token-chip guest"
          title="مانده توکن"
          aria-label={`${formatTokenFa(balance)} توکن باقی‌مانده`}
        >
          <Coins size={12} aria-hidden />
          <strong>{formatTokenFa(balance)}</strong>
        </span>
        <button
          type="button"
          className="icon-btn primary chrome-login-btn"
          onClick={() => openLogin()}
        >
          <LogIn size={15} />
          ورود
        </button>
      </div>
    );
  }

  const balance = getDisplayTokenBalance({
    isAuthenticated: true,
    userId: user?.id,
  });

  return (
    <div className="chrome-auth" ref={rootRef}>
      <span
        className="token-chip chrome-token-chip"
        title="مانده توکن"
        aria-label={`${formatTokenFa(balance)} توکن باقی‌مانده`}
      >
        <Coins size={12} aria-hidden />
        <strong>{formatTokenFa(balance)}</strong>
      </span>
      <button
        type="button"
        className="chrome-account-btn"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => setMenuOpen((o) => !o)}
        title="حساب کاربری"
      >
        <UserRound size={15} aria-hidden />
        <span>{user?.firstName || "حساب من"}</span>
      </button>
      {menuOpen ? (
        <div className="chrome-auth-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="chrome-auth-menu-item"
            onClick={() => {
              setMenuOpen(false);
              onOpenProfile?.();
            }}
          >
            <UserRound size={16} />
            نمایش حساب کاربری
          </button>
          <button
            type="button"
            role="menuitem"
            className="chrome-auth-menu-item danger"
            onClick={() => {
              setMenuOpen(false);
              void logout();
            }}
          >
            <LogOut size={16} />
            خروج
          </button>
        </div>
      ) : null}
    </div>
  );
}

type ProfileProps = {
  open: boolean;
  onClose: () => void;
};

export function ProfileSheet({ open, onClose }: ProfileProps) {
  const { user, logout, isAuthenticated } = useAuth();
  if (!open || !isAuthenticated || !user) return null;
  const balance = getDisplayTokenBalance({
    isAuthenticated: true,
    userId: user.id,
  });

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sheet-card profile-sheet"
        role="dialog"
        aria-label="حساب کاربری"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head">
          <h2>حساب کاربری</h2>
          <button type="button" className="icon-btn" onClick={onClose}>
            بستن
          </button>
        </header>
        <div className="profile-sheet-body">
          <div className="hero-art" style={{ maxWidth: "10rem" }}>
            <img src="/illustrations/welcome-hero.svg" alt="نمای حساب کاربری" />
          </div>
          <p className="profile-name">
            {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
              "کاربر نویساین"}
          </p>
          <p className="settings-hint">{user.organizationName}</p>
          {user.phone ? (
            <p className="settings-hint" dir="ltr">
              {user.phone}
            </p>
          ) : null}
          <div className="token-balance-card">
            <Coins size={18} />
            <div>
              <strong>{formatTokenFa(balance)}</strong>
              <span>توکن</span>
            </div>
          </div>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              void logout();
              onClose();
            }}
          >
            خروج از حساب
          </button>
        </div>
      </div>
    </div>
  );
}
